// Portable workflows: the idea, and the format (ocs-workflow-export/1) as a reader needs it to
// read, write or run one. docs/12 is the engineering contract; this page is its public face.
import { ArrowRight } from "@phosphor-icons/react";
import { Link } from "react-router";

import { useTitle } from "../ui/hooks";
import { PageHero, SectionHead } from "../ui/Section";

const EXAMPLE = `{
  "format": "ocs-workflow-export/1",
  "workflow": { "slug": "hourly-error-triage", "name": "Hourly Error Triage", "description": "…" },
  "skills": [{ "slug": "triage-rules", "name": "Triage rules", "files": [{ "path": "SKILL.md", "content": "…" }] }],
  "version": {
    "changelog": "Adds a guard: no new errors, no agent run.",
    "requirements": [
      { "kind": "credential", "name": "jira", "credential_kind": "jira_token", "required": true },
      { "kind": "credential", "credential_kind": "anthropic", "required": false, "platform_default": true }
    ],
    "skills": ["triage-rules"],
    "definition": {
      "start": "triage",
      "nodes": [
        { "id": "triage", "kind": "prompt", "name": "Triage new errors", "model": "sonnet",
          "prompt": "Group the new errors, find duplicates, comment on the tickets they belong to…",
          "pre_execution": { "language": "python", "source": "…exit 3 when nothing is new…" },
          "tools": [{ "tool": "jira", "actions": ["read", "comment"] }],
          "outputs": ["severity"] },
        { "id": "page", "kind": "approval", "name": "Page the on-call?",
          "question": "A P1 was found. Page the on-call engineer?", "options": ["page", "hold"] },
        { "id": "done", "kind": "end", "result": "succeeded" }
      ],
      "edges": [
        { "from": "triage", "to": "page", "when": { "output": { "key": "severity", "equals": "p1" } } },
        { "from": "triage", "to": "done", "when": "otherwise" },
        { "from": "page", "to": "done", "when": "otherwise" }
      ]
    }
  }
}`;

const PARTS = [
  ["Steps", "prompt (an agent at work), approval (a person decides), orchestrate (a parent runs its sub-tasks), end (succeeded or failed)."],
  ["Branches", "Each step's ways out, tried in order: on its outcome, on an output it reported (verdict = pass), on an approval's answer, or otherwise. Loops are allowed, each step has a visit limit."],
  ["Guards", "A pre-execution script before an agent step: exit 3 skips the step without spending a token. It may make read-only calls, named in the file, through the broker."],
  ["Tools", "The broker actions each agent step may take, listed per step. The keys are never in the file, and never in the agent."],
  ["Requirements", "What a team must bind before running: which credentials (by kind and name), and whether it needs its own virtual machine."],
  ["Skills", "Instruction files the agents read, shipped inside the document so a version always carries the knowledge it was tested with."],
  ["Version", "A changelog, and immutability: a published version never changes. Teams pin one and upgrade on purpose."],
] as const;

const PRINCIPLES = [
  ["Readable before runnable", "Anyone can open the file and see every prompt, tool, guard and approval before it touches their systems."],
  ["Declared, not discovered", "What a workflow needs and what it may do are written down, so a reviewer can say no in advance."],
  ["People at the hinges", "Outward and irreversible steps sit behind approvals. The format makes them visible."],
  ["Guards first", "The cheapest token is the one never spent. The format gives guards a first-class place."],
  ["Nobody's lock-in", "An open document any platform can implement. OCS runs it today; we want others to."],
] as const;

export function PortableWorkflows() {
  useTitle("Portable workflows");
  return (
    <>
      <PageHero kicker="§ Portable workflows · format v1" title={<>One document. Every step in the open.</>}>
        A portable workflow is an OCS idea for the world: a single, versioned document that says exactly what an AI automation will do, what it needs, and where a person must say
        yes, so it can be shared, reviewed and run anywhere that speaks the format.
      </PageHero>
      <section className="wrap grid gap-12 py-16 lg:grid-cols-[1fr_1.2fr]">
        <div>
          <SectionHead kicker="§ Why" title="Automation you can inspect.">
            Today every company writes the same AI automations in private and learns the same lessons the expensive way. A portable format turns those lessons into something you can hand
            over, review and improve together, with the changelog to prove it.
          </SectionHead>
          <ul className="mt-8 space-y-5">
            {PRINCIPLES.map(([t, d]) => (
              <li key={t}>
                <p className="font-display text-lg font-bold tracking-tight">{t}</p>
                <p className="text-[15px] text-ink2">{d}</p>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <p className="kicker">An example, trimmed</p>
          <pre className="mt-3 max-h-[640px] overflow-auto rounded-2xl bg-ink p-5 font-mono text-[12px] leading-relaxed text-paper">{EXAMPLE}</pre>
        </div>
      </section>
      <section className="border-y border-line bg-paper2/50">
        <div className="wrap py-16">
          <SectionHead kicker="§ What's in the file" title="Seven parts." />
          <dl className="mt-10 grid gap-4 md:grid-cols-2">
            {PARTS.map(([t, d]) => (
              <div key={t} className="card p-6">
                <dt className="font-mono text-[12px] font-semibold uppercase tracking-wider text-signal">{t}</dt>
                <dd className="mt-2 text-[15.5px] text-ink2">{d}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>
      <section className="wrap py-16">
        <div className="grid gap-8 rounded-3xl bg-ink p-8 text-paper sm:p-12 lg:grid-cols-[1.4fr_1fr] lg:items-center">
          <div>
            <p className="font-mono text-[11.5px] uppercase tracking-[0.16em] text-paper/50">Open for comment</p>
            <p className="display mt-3 text-4xl sm:text-5xl">Version 1 is what OCS runs today. Version 2 should be written with you.</p>
            <p className="mt-4 max-w-xl text-paper/70">What we want to add next: signed versions, so you know who published what, and a Danger Scale level declared for every tool a step may use.</p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row lg:flex-col">
            <Link to="/workflows" className="btn-signal">
              Browse published workflows <ArrowRight weight="bold" className="h-4 w-4" />
            </Link>
            <Link to="/tools/validator" className="btn border border-paper/30 text-paper hover:bg-paper hover:text-ink">
              Validate a file
            </Link>
            <Link to="/community" className="btn border border-paper/30 text-paper hover:bg-paper hover:text-ink">
              Help shape v2
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
